import { OpenInNew, StarBorderRounded, Warning } from "@mui/icons-material";
import { Rating, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import useService from "../../../../hooks/useService";
import HNSColors from "../../../ui library/HNSColors";
import { getModalHandler } from "../../Common/Modals/hooks/useModalSingleton";
import { getInNetworkColor } from "../provider-management/helper/functions";

import { PROVIDER_DETAIL_STYLES } from "./styles";

function getNetworkStatusLabel(inNetwork) {
  return inNetwork ? "In Network" : "Out Of Network";
}

function normalizeBooleanFlag(value) {
  if (value === true || value === false) {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();

    if (normalized === "true") {
      return true;
    }

    if (normalized === "false") {
      return false;
    }
  }

  return Boolean(value);
}

export default function ProviderDetailHeader({
  canEditProvider,
  canViewProvider,
  handleViewAllClients,
  shouldShowViewAllClients,
  providerInformation,
  onRatingUpdated,
}) {
  const providerRepository = useService("repositories/provider-repository");
  const modalHandler = getModalHandler?.() ?? {};
  const { openFailureModal = () => {}, openSuccessModal = () => {} } = modalHandler;
  const providerId = providerInformation?.pk || providerInformation?.id;
  const providerName = providerInformation?.name || "Provider Details";
  const inNetwork = normalizeBooleanFlag(
    providerInformation?.inNetwork ?? providerInformation?.in_network
  );
  const receivingClients = normalizeBooleanFlag(
    providerInformation?.receivingClients ?? providerInformation?.receiving_clients
  );
  const providerRating = Number(providerInformation?.rating) || 0;
  const [badgeBgColor, defaultBadgeTextColor] = getInNetworkColor(inNetwork, receivingClients);
  const badgeTextColor =
    inNetwork && !receivingClients ? HNSColors.warning[500] : defaultBadgeTextColor;
  const badgeBackgroundColor =
    inNetwork && !receivingClients ? HNSColors.warning[600] : badgeBgColor;
  const providerNameRef = useRef(null);
  const [showProviderNameTooltip, setShowProviderNameTooltip] = useState(false);

  useEffect(() => {
    const titleElement = providerNameRef.current;

    if (!titleElement) {
      return;
    }

    setShowProviderNameTooltip(titleElement.scrollHeight > titleElement.clientHeight);
  }, [providerName]);

  const handleRatingChange = (event, value) => {
    providerRepository
      .patch(providerId, { rating: value })
      .then(() => {
        onRatingUpdated?.(value);
        openSuccessModal();
      })
      .catch((error) => {
        openFailureModal(error);
      });
  };

  return (
    <>
      <div
        className="flex w-full flex-col gap-4 rounded-[12px] px-6 py-5 md:flex-row md:items-center md:justify-between"
        style={PROVIDER_DETAIL_STYLES.headerBar}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start gap-3">
            <Tooltip title={showProviderNameTooltip ? providerName : ""}>
              <h2
                ref={providerNameRef}
                className="max-w-full break-words text-2xl font-semibold leading-tight"
                style={PROVIDER_DETAIL_STYLES.headerTitle}
              >
                {providerName}
              </h2>
            </Tooltip>
            <div
              className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold leading-none"
              style={PROVIDER_DETAIL_STYLES.headerStatusBadge(
                badgeBackgroundColor,
                badgeTextColor
              )}
            >
              {getNetworkStatusLabel(inNetwork)}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-3">
            {canViewProvider ? (
              <div className="flex items-center gap-3">
                <Rating
                  name={`${providerId}`}
                  value={providerRating}
                  className="flex justify-center"
                  disabled={!canEditProvider}
                  onChange={handleRatingChange}
                  sx={PROVIDER_DETAIL_STYLES.headerRating}
                  emptyIcon={
                    <StarBorderRounded
                      fontSize="inherit"
                      sx={{ color: HNSColors.neutrals[900] }}
                    />
                  }
                />
                {providerInformation?.rating === null ? (
                  <Tooltip title="Not yet rated">
                    <Warning sx={PROVIDER_DETAIL_STYLES.headerRatingEmptyIcon} />
                  </Tooltip>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 flex-row flex-wrap items-center gap-3 md:justify-end">
          {shouldShowViewAllClients ? (
            <button
              id="provider-detail-view-all-clients-button"
              type="button"
              onClick={handleViewAllClients}
              style={PROVIDER_DETAIL_STYLES.headerActionButton}
            >
              View All Clients
              <OpenInNew style={PROVIDER_DETAIL_STYLES.headerActionIcon} />
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
}

ProviderDetailHeader.propTypes = {
  canEditProvider: PropTypes.bool.isRequired,
  canViewProvider: PropTypes.bool.isRequired,
  handleViewAllClients: PropTypes.func.isRequired,
  onRatingUpdated: PropTypes.func,
  providerInformation: PropTypes.object.isRequired,
  shouldShowViewAllClients: PropTypes.bool.isRequired,
};
